// User Types
export interface User {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isEmailVerified: boolean;
  createdAt: string;
  lastLoginAt?: string;
  preferences?: UserPreferences;
  avatar?: string;
  locale?: string;
  timezone?: string;
  /**
   * Freies JSON aus der Backend-Spalte `user.profile` — dort lebt u. a. `bio`. Das Backend
   * liefert es in `GET /api/users/me` mit; im CMS-Typ fehlte es bisher, weshalb das
   * Biografie-Feld im Profil immer leer blieb.
   */
  profile?: Record<string, unknown>;
}

/**
 * Erlaubte Felder für `PUT /api/users/me`, 1:1 nach `UserValidation.updateProfile`.
 * `email` fehlt bewusst — siehe `apiService.updateCurrentUser`.
 */
export interface UpdateProfilePayload {
  firstName?: string;
  lastName?: string;
  username?: string;
  locale?: string;
  timezone?: string;
  profile?: Record<string, unknown>;
}

export enum UserRole {
  ADMIN = 'ADMIN',
  PREMIUM = 'PREMIUM',
  FREE = 'FREE',
  MODERATOR = 'MODERATOR',
  CONTENT_CREATOR = 'CONTENT_CREATOR'
}

export interface UserPreferences {
  notifications: {
    email: boolean;
    push: boolean;
  };
  privacy: {
    profileVisible: boolean;
  };
  content: {
    language: string;
    timezone: string;
  };
  accessibility: {
    highContrast: boolean;
    largeText: boolean;
  };
}

// Content Types
export interface Content {
  id: string;
  title: string;
  description: string;
  shortDescription?: string; // Kurzbeschreibung
  duration?: string; // Dauer
  headphones?: HeadphonesRecommendation; // Kopfhörer-Empfehlung
  keywords?: string; // Keywords
  type: ContentType;
  status: ContentStatus;
  category?: string;
  /** Unterthemen-Slugs. Seit die Taxonomie in der Datenbank liegt (PR #623) sind das
   *  Laufzeit-Werte — ein Compile-Zeit-Enum kann sie nicht mehr abbilden. Gültigkeit
   *  sichert die Auswahl-Komponente, nicht der Typ. */
  topicCategories?: string[];
  tags: string[];
  authorId: string;
  content: string;
  metadata?: ContentMetadata;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  playCount: number;
  averageRating?: number;
  isFeatured: boolean;
  author?: {
    username: string;
  };

  // ——— Prisma-Spalten des Content-Modells, die die API mitliefert ———
  // Standen bisher nicht im Interface, weshalb ContentManagement.tsx sie über `as any` las.
  // Belegt an prisma/schema.prisma (model Content): language, isPremium, contentData.
  language?: string;
  isPremium?: boolean;
  contentData?: ContentData;
}

/**
 * Die `contentData`-Spalte (Prisma Json). Pro Content-Typ ein eigener Block; das Backend
 * reicht sie unverändert durch (ContentController normalisiert nur blog.content auf '').
 * Hier steht nur der Blog-Block, weil ausschließlich er im CMS gelesen wird.
 */
export interface ContentData {
  blog?: {
    content?: string;
    heroImage?: string;
    subtitle?: string;
    teaserText?: string;
  };
}

/**
 * Inhaltstypen — spiegelt EXAKT das Prisma-Enum `ContentType` des Backends.
 *
 * ⚠️ Die Werte sind GROSS geschrieben, und das ist keine Kosmetik. Bis 2026-08-12 standen hier
 * Kleinbuchstaben, während die API groß verlangt und liefert. Am laufenden System gemessen:
 * `GET /api/content?type=audio` → **400**, `type=AUDIO` → 200. Der Typ-Filter war damit kaputt
 * (jede Wahl lief in den `catch` und leerte die Liste), Publish-/Archiv-Knöpfe rendeten nie,
 * Status-Abzeichen und Typ-Symbole fielen auf den Standardfall.
 *
 * ⚠️ `WEB_COMPONENT` stand hier früher, existiert aber in der Datenbank NICHT — es ist ein
 * Formular-Fluss, kein Inhaltstyp (siehe `ContentEditorFlow` unten). `MINDBOOK` fehlte umgekehrt,
 * obwohl die DB es kennt und nutzt.
 *
 * Wer diese Liste ändert, ändert einen Vertrag: `backend/prisma/schema.prisma` `enum ContentType`.
 */
export enum ContentType {
  AUDIO = 'AUDIO',
  VIDEO = 'VIDEO',
  BLOG = 'BLOG',
  SHOP = 'SHOP',
  AFFIRMATION = 'AFFIRMATION',
  MINDBOOK = 'MINDBOOK'
}

/**
 * Welches Formular zum Bearbeiten geöffnet wird — reine Oberflächen-Entscheidung.
 *
 * Der Web-Komponenten-Editor bleibt erhalten (Tom-Entscheidung 2026-08-12); gespeichert wird das
 * Ergebnis als `ContentType.BLOG`. Früher lief diese Unterscheidung über einen Phantom-Wert im
 * Inhaltstyp-Enum — dadurch filterte die Oberfläche auf einen Wert, den die Datenbank nie enthält.
 */
export enum ContentEditorFlow {
  STANDARD = 'standard',
  WEB_COMPONENT = 'web_component'
}

/**
 * Status — spiegelt das Prisma-Enum `ContentStatus` VOLLSTÄNDIG. Siehe Warnung bei `ContentType`.
 *
 * ⚠️ `REJECTED` fehlte hier bis 2026-08-12, obwohl das Backend-Enum es kennt. Ein abgelehnter
 * Inhalt fiel damit im CMS auf den Standardfall und zeigte den rohen Wert.
 *
 * ⚠️ **Das ist das EINZIGE Status-Enum für Inhalte.** Bis 2026-08-13 stand es dreimal daneben —
 * `AudioStatus`, `VideoStatus`, `MindBookStatus` —, und alle drei kannten `REJECTED` nicht.
 * Warum das keine Geschmacksfrage ist: Audio, Video und MindBooks SIND Content-Zeilen
 * (`content.type`), das Schema kennt kein zweites Status-Feld, und alle vier Routen schicken
 * ihren Status durch dieselbe Backend-Funktion `normalizeStatus` (`services/contentMapping.ts`,
 * geteilt von `audioMapping`/`videoMapping`/`mindBookMapping`). Ein abgelehnter Audio-Inhalt war
 * im CMS deshalb nicht darstellbar — dieselbe Klasse wie das zweite `UserRole`-Enum ohne
 * `CONTENT_CREATOR`, das einer ganzen Rolle drei Menüpunkte ausgeblendet hat.
 *
 * ⚠️ `ARCHIVED` und `REJECTED` schreibt das CMS selbst nicht: `REJECTED` setzt die Prüfung über
 * `POST /api/content/:id/reject` (Backend-Route, gesperrt auf ADMIN/MODERATOR), das CMS ruft sie
 * nirgends auf. Sie bleiben trotzdem im Enum, denn die Werte stehen so in der Datenbank und
 * kommen über die Listen herein. Ein Enum, das nur kennt, was das CMS gerade selbst schreibt,
 * wäre kein Abbild des Vertrags mehr — die Anzeige einer archivierten oder abgelehnten Zeile
 * fiele still auf den Standardfall zurück.
 *
 * ⚠️ NICHT hierher gehören `AlbumStatus` und `ArtistStatus`: das sind laut `schema.prisma` eigene
 * Prisma-Enums mit nur DRAFT/PUBLISHED. Und der „Status" der Benutzer-Seite
 * (`'active' | 'inactive'`) ist ein anderer Begriff mit demselben Namen.
 */
export enum ContentStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
  REJECTED = 'REJECTED'
}

/**
 * Anzeigetexte und Farbton je Status — die EINE Quelle für alle Verwaltungsseiten.
 *
 * ⚠️ Vorher hatte jede Seite ihre eigene `getStatusBadge`-Funktion, und die Beschriftungen waren
 * bereits auseinandergelaufen: Audio und Video sagten „Veröffentlicht", die Inhalte-Seite
 * „Published". Genau das Kopier-Muster, das sich im CMS an mehreren Stellen wiederholt.
 *
 * Die Schlüssel sind Strings statt `ContentStatus`, weil hier auch ein Wert nachgeschlagen wird,
 * der roh aus der Antwort kommt (`StatusBadge` bekommt einen `string`). Ein unbekannter Schlüssel
 * ergibt `undefined` — die Abzeichen-Komponente zeigt dann den rohen Wert, statt eine leere
 * Fläche zu rendern.
 */
export const STATUS_ANZEIGE: Record<string, { text: string; ton: string }> = {
  DRAFT: { text: 'Entwurf', ton: 'badge-warning' },
  PUBLISHED: { text: 'Veröffentlicht', ton: 'badge-success' },
  ARCHIVED: { text: 'Archiviert', ton: 'badge-secondary' },
  REJECTED: { text: 'Abgelehnt', ton: 'badge-error' },
};

/**
 * Die Stufen, die ein Autor im FORMULAR selbst setzen darf — bewusst nicht alle.
 *
 * ⚠️ `REJECTED` fehlt hier mit Absicht, und das ist keine Bequemlichkeit: abgelehnt wird über
 * `POST /api/content/:id/reject`, und diese Backend-Route ist auf ADMIN/MODERATOR gesperrt
 * (Gegenstück: `/approve`). Den Status setzt also eine Prüfung, nicht der Verfasser. Stünde
 * „Abgelehnt" im Formular zur Wahl, könnte ein Redakteur seinen eigenen Inhalt an der Prüfung
 * vorbei in genau den Zustand setzen, den die Prüfung vergibt.
 *
 * ⚠️ Unterschied zum FILTER (`statusAuswahl`), der alle vier Stufen führt: Finden ist nicht
 * Setzen. Ein abgelehnter Inhalt muss auffindbar und ANZEIGBAR bleiben — deshalb ist das Enum
 * vollständig und die Formulare zeigen eine bereits gesetzte Ablehnung als gesperrten Eintrag.
 * Ohne diesen Eintrag stünde im Auswahlfeld eines abgelehnten Datensatzes gar nichts (ein
 * `<select>` ohne passende Option zeigt eine leere Zeile) — unsichtbar falsch statt sichtbar.
 */
export const STATUS_WAEHLBAR = [
  ContentStatus.DRAFT,
  ContentStatus.PUBLISHED,
  ContentStatus.ARCHIVED,
] as const satisfies readonly ContentStatus[];

/**
 * Anzeigetexte je Benutzerrolle — dieselbe Rolle wie `STATUS_ANZEIGE`, nur für
 * die Rolle. Die Enum-Werte sind Vertragswerte zum Backend (Prisma `enum UserRole`), keine
 * Anzeigetexte; ohne diese Karte stünde „CONTENT_CREATOR" in der Oberfläche.
 *
 * `satisfies`: beide Zugriffsstellen sind `UserRole`, also erzwingt der
 * Compiler Vollständigkeit — eine neue Rolle im Enum macht diese Karte rot, statt still einen
 * rohen Wert in die Liste zu schreiben.
 *
 * ⚠️ `PREMIUM`/`FREE` bleiben englisch. Es sind die Zugriffsstufen, die im CMS bereits so
 * beschriftet sind (Spalte „Zugriff" in der Audio- und Video-Liste) — zwei Namen für dieselbe
 * Stufe wären schlimmer als ein unübersetzter.
 */
export const ROLLEN_ANZEIGE = {
  ADMIN: 'Administrator',
  MODERATOR: 'Moderator',
  CONTENT_CREATOR: 'Redaktion',
  PREMIUM: 'Premium',
  FREE: 'Free',
} satisfies Record<UserRole, string>;

// Kopfhörer-Empfehlung Enum
export enum HeadphonesRecommendation {
  RECOMMENDED = 'recommended', // Empfohlen
  NOT_NECESSARY = 'not_necessary', // Nicht notwendig
  NO = 'no' // Nein
}


// ⚠️ Hier standen bis 2026-08-11 `MainTopicCategory`, `SubTopicCategory`, `MainTopicToSubTopics`,
// `MainTopicLabels`, `SubTopicLabels` und der Alias `TopicCategory` — 48 Themen als fester
// Union-Typ plus zwei handgepflegte Label-Karten. Genau diese Zweitliste war der Defekt:
// gleichzeitig existierte eine Pflege-Oberfläche mit eigener Liste und in premium-web eine dritte,
// bereits auseinandergelaufene (37 statt 48 Einträge).
//
// Seit PR #623 liegt die Taxonomie in der Datenbank und ist die EINE Quelle:
//   Auswahl    → useTopicTaxonomy / TopicCategoryPicker
//   Pflege     → TopicCategoryManagement
//   Filterwert → schlicht `string` (siehe types/audio.ts, types/video.ts)
// Der Block wurde ersatzlos entfernt, nachdem der letzte Verweis darauf weg war.
// Plan: docs/plans/plan-themen-kategorien-2026-08.md

// Labels für Kopfhörer-Empfehlungen
export const HeadphonesLabels: Record<HeadphonesRecommendation, string> = {
  [HeadphonesRecommendation.RECOMMENDED]: 'Empfohlen',
  [HeadphonesRecommendation.NOT_NECESSARY]: 'Nicht notwendig',
  [HeadphonesRecommendation.NO]: 'Nein'
};

export interface ContentMetadata {
  // Anzeige-Text („10 Minuten"), KEINE Sekunden — ContentManagement schreibt formData.duration
  // (ein Textfeld) hier hinein. Die früher deklarierte `number` war schlicht falsch und nur
  // deshalb nie aufgefallen, weil die Nutzlast als `any` an den Service ging. Gleiche Falle
  // wie AudioMetadata.duration.
  duration?: string;
  fileSize?: number;
  format?: string;
  resolution?: string;
  thumbnail?: string;
  // Blog Post specific metadata
  subtitle?: string;
  heroImage?: string;
  teaserText?: string;
  // Von ContentManagement.handleFormSubmit in `metadata` geschrieben und von dort auch wieder
  // gelesen — bisher nur über die Index-Signatur (also als `any`) erreichbar.
  shortDescription?: string;
  headphones?: HeadphonesRecommendation | null;
  keywords?: string;
  /** Unterthemen-Slugs. Seit die Taxonomie in der Datenbank liegt (PR #623) sind das
   *  Laufzeit-Werte — ein Compile-Zeit-Enum kann sie nicht mehr abbilden. Gültigkeit
   *  sichert die Auswahl-Komponente, nicht der Typ. */
  topicCategories?: string[];
  // `metadata` ist eine freie JSON-Spalte: was oben nicht steht, ist ungeprüft. `unknown` statt
  // `any`, damit ein neuer Lesezugriff sich zum Narrowing bekennen muss.
  [key: string]: unknown;
}

// API Response Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
  code?: string;
  details?: unknown;
  requestId?: string;
  timestamp?: string;
}

/**
 * Einzige Definition der paginierten Listen-Hülle im CMS.
 * `types/audio.ts`, `types/video.ts` und `types/mindbook.ts` re-exportieren nur noch von hier.
 *
 * `success` ist optional, weil es allein `apiService.getContent()` durchreicht — die
 * Medien-Services (Audio/Video/MindBook) haben es nie mitgeliefert. Gelesen wird das Feld
 * von keinem Konsumenten; required zu lassen hätte die drei Medien-Services gebrochen.
 */
export interface PaginatedResponse<T> {
  success?: boolean;
  data: T[];
  pagination: Pagination;
}

/** Paginierung in der Form, die der UI-Code konsumiert — alle Felder garantiert gesetzt. */
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Paginierung so, wie das Backend sie tatsächlich über die Leitung schickt.
 *
 * Am Backend-Code verifiziert (energetic-eternity-backend):
 *   /api/audio, /api/video, /api/mindbook → { page, limit, total, totalPages }
 *   /api/content (ContentService)         → { page, limit, total, pages }
 *
 * Die Seitenzahl heißt also je nach Endpunkt anders. Beide Felder sind deshalb optional;
 * `normalizePagination()` gleicht das an, damit im UI-Code nur noch `Pagination` ankommt.
 */
export interface ApiPagination {
  page?: number;
  limit?: number;
  total?: number;
  /** /api/audio, /api/video, /api/mindbook */
  totalPages?: number;
  /** /api/content — gleiche Bedeutung, anderer Name */
  pages?: number;
}

/** Fallback, wenn das Backend gar keine Paginierung mitschickt. */
const EMPTY_PAGINATION: Pagination = { page: 1, limit: 10, total: 0, totalPages: 0 };

/**
 * Bringt die Wire-Form auf `Pagination`.
 * Ersetzt die bisher viermal kopierte `|| { page: 1, limit: 10, total: 0, totalPages: 0 }`-Zeile
 * und übersetzt `pages` (Content-Endpunkt) auf `totalPages`.
 */
export function normalizePagination(raw?: ApiPagination | null): Pagination {
  if (!raw) return { ...EMPTY_PAGINATION };
  return {
    page: raw.page ?? EMPTY_PAGINATION.page,
    limit: raw.limit ?? EMPTY_PAGINATION.limit,
    total: raw.total ?? EMPTY_PAGINATION.total,
    totalPages: raw.totalPages ?? raw.pages ?? EMPTY_PAGINATION.totalPages,
  };
}

// Auth Types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  acceptTerms: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
}

// Hier standen `FileUpload` und `FileType`: eine Datei-Upload-Hülle samt Datei-Art-Enum,
// die nichts im CMS je erzeugt oder gelesen hat. Datei-Uploads laufen über den Bunny-Pfad
// (`bunnyCDNService` mit `BunnyFile`), nicht über diese Form.

// Analytics Types
export interface ContentAnalytics {
  contentId: string;
  views: number;
  downloads: number;
  ratings: {
    average: number;
    count: number;
    distribution: Record<string, number>;
  };
  engagement: {
    timeSpent: number;
    completionRate: number;
  };
}

// `UserAnalytics` stand hier neben `ContentAnalytics` — pro Benutzer statt pro Inhalt.
// Anders als das Gegenstück hatte es weder einen Endpunkt noch einen Leser: die Übersicht
// zeigt `DashboardStats`, die Benutzerverwaltung zählt nichts. Gelöscht.

// Dashboard Types
export interface DashboardStats {
  totalUsers: number;
  totalContent: number;
  totalRevenue: number;
  recentActivity: ActivityLog[];
  // ⚠️ `systemHealth` ist hier entfallen: das Feld stand als PFLICHT im Vertrag, kam aber im
  // gesamten Backend nicht vor. Ein Vertrag, der etwas verlangt, das nie geliefert wird, ist
  // keine Zusage, sondern eine Behauptung.
  //
  // ⚠️ `totalViews`/`totalDownloads` sind im selben Schnitt entfallen, samt Endpunkt
  // (`AdminService.getDashboard`): `ContentView` hat keine Schreibstelle, `Download` zwei — aber
  // keinen Aufrufer, und laut Erlösentscheidung kommt keiner. Ausführlich in `Dashboard.tsx`.
}

export interface ActivityLog {
  id: string;
  type: string;
  userId: string;
  timestamp: string;
  // Freie JSON-Spalte; kein Konsument im CMS liest heute einzelne Schlüssel.
  details: Record<string, unknown>;
}

// Form Types
export interface ContentFormData {
  title: string;
  description: string;
  shortDescription?: string; // Kurzbeschreibung
  duration?: string; // Dauer
  headphones?: HeadphonesRecommendation; // Kopfhörer-Empfehlung
  keywords?: string; // Keywords
  type: ContentType;
  category?: string;
  /** Unterthemen-Slugs. Seit die Taxonomie in der Datenbank liegt (PR #623) sind das
   *  Laufzeit-Werte — ein Compile-Zeit-Enum kann sie nicht mehr abbilden. Gültigkeit
   *  sichert die Auswahl-Komponente, nicht der Typ. */
  topicCategories?: string[];
  tags: string[];
  content: string;
  language?: string;
  isPremium?: boolean;
  metadata?: ContentMetadata;
}

// Was BEIM SCHREIBEN wirklich über die Leitung geht — nicht zu verwechseln mit
// `ContentFormData`, das die Formular-Sicht beschreibt. Die beiden unterscheiden sich real:
// `type` ist hier GROSS (Prisma-Enum), und `slug`, `locale` sowie `contentData` kommen dazu.
// `apiService.createContent`/`updateContent` deklarierten bisher `ContentFormData` — eine
// Signatur, die zur aufrufenden Seite nie gepasst hat. Sichtbar wurde das nicht, weil der
// Aufrufer sein Objekt als `any` weiterreichte.
export interface ContentWritePayload {
  title: string;
  description?: string;
  type: string; // Prisma-ContentType in GROSSBUCHSTABEN, z. B. 'BLOG'
  slug?: string;
  category?: string;
  tags?: string[];
  locale?: string;
  language?: string;
  isPremium?: boolean;
  content?: string;
  contentData?: ContentData;
  metadata?: ContentMetadata;
}

// `UserFormData` ist hier entfallen: die Benutzerverwaltung legt keine Benutzer an — sie
// listet, wechselt Rolle und Status. Ein Formularvertrag für ein Formular, das es nicht
// gibt, beschreibt nichts.

// Filter Types
export interface ContentFilters {
  page?: number;
  limit?: number;
  type?: ContentType;
  status?: ContentStatus;
  category?: string;
  tag?: string;
  search?: string;
  authorId?: string;
}

/**
 * Filter der Benutzer-Liste — 1:1 die Query-Parameter, die `GET /api/users` auswertet
 * (`UserController.getAllUsers` → `BaseUserQueryService.buildUserWhereClause`).
 *
 * ⚠️ `status` kannte hier bis 2026-08-12 zusätzlich `'suspended'`. Das war ein Phantom-Wert: das
 * Backend schreibt `isActive = (status === 'active' || status === 'true')` — jeder andere String
 * landet also stumm auf „inaktiv". Ein Menüpunkt „Gesperrt" hätte dieselbe Liste geliefert wie
 * „Inaktiv" und einen Zustand versprochen, den es nicht gibt.
 */
export interface UserFilters {
  page?: number;
  limit?: number;
  role?: UserRole;
  status?: 'active' | 'inactive';
  search?: string;
}

// Notification Types
// `Notification` ist hier entfallen: es beschrieb eine gespeicherte Benachrichtigung eines
// Benutzers — ein Postfach, das es im CMS nicht gibt. Die Balken der Oberfläche sind etwas
// anderes und tragen ihren eigenen Typ (`BenachrichtigungsTyp` in `ui/Benachrichtigung.tsx`).

// Error Types
// `ValidationError` und `ApiError` sind hier entfallen. Der Hinweis, dass sie keinen
// Konsumenten haben, stand seit der letzten Runde daneben — ein Vertrag, den niemand liest,
// beschreibt nichts und läuft nur still von der Wirklichkeit weg. Die im Code tatsächlich
// benutzten Fehler-Typen stehen in `utils/errors.ts` (`ApiValidationError`/`ApiErrorData`).
